import React, { useEffect, useState } from "react";
import GlobalHeader from "../../../components/GlobalHeader";
import {
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  tableCellClasses,
  Paper,
} from "@mui/material";
import styled from "@emotion/styled";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";
import Audio from "react-loading-icons/dist/esm/components/audio";
import { getDateAndHour } from "../../../utils/date";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    textAlign: "center",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textAlign: "center",
  },
}));

const RETIRO_LABELS = {
  PENDING: { label: "Retiro pendiente", color: "warning" },
  COMPLETED: { label: "Retiro completado", color: "default" },
};

const ResumenCard = ({ titulo, valor, color }) => (
  <Stack
    sx={{
      p: 3,
      borderRadius: "20px",
      backgroundColor: color,
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <Typography variant="h5" textAlign="center" color="white">
      $ {valor} USD
    </Typography>
    <Typography variant="body2" textAlign="center" color="white">
      {titulo}
    </Typography>
  </Stack>
);

const ContabilidadProductor = () => {
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi, loadedApi } = useApi();
  const [mainData, setMainData] = useState({
    saldo: 0,
    totalIngresos: 0,
    totalRetirado: 0,
    retirosPendientes: 0,
    movimientos: [],
  });

  useEffect(() => {
    loadApi("productor/contabilidad", true, "get")
      .then((response) => {
        setMainData(response.data);
      })
      .catch((e) => {
        showSnack(e.message, "error", false);
      });
  }, []);

  const loading = !loadedApi.includes("get__productor/contabilidad");

  return (
    <>
      <GlobalHeader />
      <Container sx={{ pb: 6 }}>
        <Stack gap={3} pt={2}>
          <Typography variant="h4" color="primary.main" fontWeight="900">
            Contabilidad
          </Typography>
          {loading ? (
            <Stack alignItems="center" py={8}>
              <Audio fill="#F27224" />
            </Stack>
          ) : (
            <>
              {/* Resumen */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <ResumenCard
                    titulo="Saldo disponible"
                    valor={mainData.saldo}
                    color="#85bb65"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ResumenCard
                    titulo="Total ganado por envíos"
                    valor={mainData.totalIngresos}
                    color="#F27224"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ResumenCard
                    titulo={
                      mainData.retirosPendientes > 0
                        ? `Total retirado ($ ${mainData.retirosPendientes} en proceso)`
                        : "Total retirado"
                    }
                    valor={mainData.totalRetirado}
                    color="#5c6bc0"
                  />
                </Grid>
              </Grid>
              {/* Movimientos */}
              <Typography variant="h5">Movimientos</Typography>
              {mainData.movimientos.length === 0 ? (
                <Stack alignItems="center" py={4}>
                  <Typography variant="h6" color="GrayText" fontWeight={400}>
                    Aún no tienes movimientos
                  </Typography>
                </Stack>
              ) : (
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Fecha</StyledTableCell>
                        <StyledTableCell>Concepto</StyledTableCell>
                        <StyledTableCell>Tipo</StyledTableCell>
                        <StyledTableCell>Monto</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mainData.movimientos.map((mov, i) => (
                        <TableRow key={i}>
                          <StyledTableCell>
                            {getDateAndHour(mov.fecha)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ textAlign: "left" }}>
                            {mov.concepto}
                          </StyledTableCell>
                          <StyledTableCell>
                            {mov.tipo === "ingreso" ? (
                              <Chip
                                label="Ingreso"
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label={
                                  RETIRO_LABELS[mov.estado]?.label || "Retiro"
                                }
                                color={
                                  RETIRO_LABELS[mov.estado]?.color || "default"
                                }
                                size="small"
                              />
                            )}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{
                              color:
                                mov.tipo === "ingreso" ? "#2e7d32" : "#c62828",
                              fontWeight: 600,
                            }}
                          >
                            {mov.tipo === "ingreso" ? "+" : "-"} $ {mov.monto}{" "}
                            USD
                          </StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Stack>
      </Container>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
};

export default ContabilidadProductor;
